import {createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {useAuth} from '@/hooks/platform/use-auth'
import {readCollapsed, writeCollapsed} from '@/lib/sidenav-collapse'
import {ChevronGlyph} from '@/components/ui/icons'
import {cn} from '@/lib/utils'

/** Contexto y no prop: entre la sección y el link hay fragmentos y ramas condicionales. */
type ReportActive = (active: boolean) => void
const SectionActiveContext = createContext<ReportActive | null>(null)

/** Componente y no hook: `isActive` solo existe en el render-prop de `NavLink`. Cuenta altas y bajas
 * para que pasar entre dos ítems de la misma sección no parpadee. */
export function SectionActiveBeacon({active}: {active: boolean}) {
  const report = useContext(SectionActiveContext)
  useEffect(() => {
    if (!report || !active) return
    report(true)
    return () => report(false)
  }, [report, active])
  return null
}

/** La sección con el ítem activo se abre sola en el flanco (sin activo → con activo), no mientras
 * dure: si no, plegar la sección en la que uno está no haría nada. */
export function SidebarSection({
  id,
  context,
  label,
  children,
}: {
  id: string
  context: 'admin' | 'tenant'
  label: string
  children: ReactNode
}) {
  const {t} = useTranslation()
  const {user} = useAuth()
  const scope = `${user?.id ?? 'anon'}.${context}`
  const [collapsed, setCollapsed] = useState(() => readCollapsed(scope, id))
  const [activeCount, setActiveCount] = useState(0)
  const contentId = useId()
  const hadActive = useRef(false)

  const report = useCallback<ReportActive>((active) => {
    setActiveCount((n) => n + (active ? 1 : -1))
  }, [])

  const hasActive = activeCount > 0
  useEffect(() => {
    if (hasActive && !hadActive.current) {
      setCollapsed(false)
      writeCollapsed(scope, id, false)
    }
    hadActive.current = hasActive
  }, [hasActive, scope, id])

  function toggle() {
    const next = !collapsed
    setCollapsed(next)
    writeCollapsed(scope, id, next)
  }

  return (
    <div className="mt-5">
      <button
        type="button"
        data-testid={`nav-section-${id}`}
        aria-expanded={!collapsed}
        aria-controls={contentId}
        title={t(collapsed ? 'nav.expandSection' : 'nav.collapseSection')}
        onClick={toggle}
        className="sidenav-section sidenav-section-toggle mb-1 flex min-h-11 w-full cursor-pointer items-center gap-1.5 px-2.5 py-1 text-left font-mono text-[10px] font-semibold tracking-[0.12em] uppercase outline-none focus-visible:ring-2 focus-visible:ring-ring/50 md:min-h-0">
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <ChevronGlyph className={cn('opacity-70', collapsed && '-rotate-90')} />
      </button>
      <div id={contentId} data-open={!collapsed} className="sidenav-collapsible">
        {/* `py-0.5`: el contenedor recorta y sin esos 2px se cortaría el anillo de foco. */}
        <div className="flex flex-col gap-0.5 py-0.5">
          <SectionActiveContext.Provider value={report}>{children}</SectionActiveContext.Provider>
        </div>
      </div>
    </div>
  )
}
